import { PasswordInput } from "@/components/password-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { register } from "@/modules/authentication/api/api-auth";
import { AlertCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "@/api/api-axios";
import ZaloLoginButton from "./ZaloLoginButton";
import GoogleLoginButton from "./GoogleLoginButton";
export function RegisterForm({
  className,
  ...props
}: React.ComponentProps<"form">) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(""); // Clear error when user starts typing
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError("Vui lòng điền đầy đủ thông tin!");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Mật khẩu xác nhận không khớp!");
      return false;
    }

    if (formData.password.length < 6) {
      setError("Mật khẩu phải có ít nhất 6 ký tự!");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await register({
        email: formData.email,
        password: formData.password,
      });
      console.log(response);
      // Handle successful registration
      if (response.success) {
        navigate("/login", {
          state: {
            message: "Đăng ký thành công! Vui lòng đăng nhập.",
          },
        });
      } else {
        // Handle API error response with success: false
        const errorData = response.data as { message?: string };
        setError(errorData?.message || "Đăng ký thất bại!");
      }
    } catch (err: unknown) {
      const apiError = err as ApiError;
      setError((apiError.data?.message as string) || "Đăng ký thất bại!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      className={cn(
        "flex flex-col gap-6 p-6 bg-white rounded-lg border border-gray-200",
        className
      )}
      {...props}
      onSubmit={handleSubmit}
    >
       <div className="flex flex-col gap-2">
        <p className="">Đăng ký tài khoản,</p>
        <h1 className="text-gray-800 text-2xl font-bold">
          Hệ thống <span className="font-bold text-primary">ABT</span>
        </h1>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="grid gap-6">
         

        <div className="grid gap-3">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Nhập email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div className="grid gap-3">
          <Label htmlFor="password">Mật khẩu</Label>
          <PasswordInput
            id="password"
            placeholder="Nhập mật khẩu"
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div className="grid gap-3">
          <Label htmlFor="confirmPassword">Xác nhận mật khẩu</Label>
          <PasswordInput
            id="confirmPassword"
            placeholder="Nhập lại mật khẩu"
            value={formData.confirmPassword}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            required
            disabled={isLoading}
          />
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Đang đăng ký...
            </>
          ) : (
            "Đăng ký"
          )}
        </Button>
      </div>

      <div className="flex items-center justify-center gap-2">
          <span className="text-muted-foreground  w-full h-[1px] bg-gray-200"></span>
          <span className="text-gray-500 text-sm text-nowrap flex-1 ">
            Đăng nhập bằng
          </span>
          <span className="text-muted-foreground w-full h-[1px] bg-gray-200"></span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ZaloLoginButton
            rememberMe={true}
            isZaloLoading={false}
            setIsZaloLoading={() => {}}
            setError={setError}
            disabled={isLoading}

          />
          <GoogleLoginButton
            rememberMe={true}
            setIsGoogleLoading={() => {}}
            setError={setError}
            disabled={isLoading}
          />
        </div>
        <div className=" text-sm">
          <span className="text-muted-foreground">
            Nếu bạn đã có tài khoản hãy đăng nhập{" "}
          </span>
          <Link to="/login" className="font-medium  underline text-primary">
            tại đây
          </Link>
        </div>
    </form>
  );
}
